import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  TablePagination,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
  Divider,
  FormControlLabel,
  Switch,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Delete,
  Search,
  Link as LinkIcon,
  LinkOff,
  Refresh,
  AdminPanelSettings,
  FlightTakeoff,
  Email,
  Business,
  Group,
  VpnKey,
  Phone,
  CheckCircle,
  Cancel,
  Warning,
  MoreVert,
  PersonAdd,
  PersonRemove,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { User, Club } from '../../types';
import RequireEmailVerification from '../../components/auth/RequireEmailVerification';
import { useTranslation } from 'react-i18next';
import ForceAllReacceptDialog from '../../components/users/ForceAllReacceptDialog';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Gavel, LocalShipping, Build } from '@mui/icons-material';
import { format } from 'date-fns';

interface UserClubAssociation {
  user_id: string;
  club_id: string;
  user_name: string;
  club_name: string;
}

interface UserJoinedClub {
  user_id: string;
  user_name: string;
  club_id: string;
  club_name: string;
}

interface GuestServiceUser {
  id: string;
  email: string;
  nickname: string;
  name: string;
  phone?: string;
  service_tier: 'free' | 'premium';
  shuttle_access: boolean;
  reminder_access: boolean;
  equipment_count: number;
  equipment_limit?: number;
  bookings_count: number;
  shuttle_bookings_limit?: number;
  terms_accepted: boolean;
  privacy_policy_accepted: boolean;
  shuttle_terms_accepted: boolean;
  email_verified: boolean;
  is_active: boolean;
  created_at: string;
  subscription_date?: string;
  preferred_language: string;
}

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [users, setUsers] = useState<User[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [associations, setAssociations] = useState<UserClubAssociation[]>([]);
  const [joinedClubs, setJoinedClubs] = useState<UserJoinedClub[]>([]);
  const [guestUsers, setGuestUsers] = useState<GuestServiceUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchUser, setSearchUser] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    id: string | null;
    name: string | null;
  }>({ open: false, id: null, name: null });
  
  // Association dialog state
  const [associationDialog, setAssociationDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedClub, setSelectedClub] = useState('');
  const [promoteToClubManager, setPromoteToClubManager] = useState(false);
  const [associating, setAssociating] = useState(false);
  const [forceAllReacceptOpen, setForceAllReacceptOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pinDialog, setPinDialog] = useState<{
    open: boolean;
    userId: string | null;
    nickname: string | null;
    newPin: string;
  }>({ open: false, userId: null, nickname: null, newPin: '' });
  const [sendingPin, setSendingPin] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  // Role menu state
  const [roleMenuAnchor, setRoleMenuAnchor] = useState<null | HTMLElement>(null);
  const [roleMenuUser, setRoleMenuUser] = useState<User | null>(null);
  const [updatingRole, setUpdatingRole] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch data - handle permissions gracefully with proper typing
      const [usersRes, clubsRes, assocRes, joinedRes, guestRes] = await Promise.all([
        api.get<User[]>('/users').catch(() => ({ data: [] as User[] })),
        api.get<Club[]>('/clubs').catch(() => ({ data: [] as Club[] })),
        api.get<UserClubAssociation[]>('/get-clubs-users').catch(() => ({ data: [] as UserClubAssociation[] })),
        api.get<UserJoinedClub[]>('/get-users-joined-clubs').catch(() => ({ data: [] as UserJoinedClub[] })),
        api.get<GuestServiceUser[]>('/service-auth/guest-users').catch(() => ({ data: [] as GuestServiceUser[] })),
      ]);

      setUsers(usersRes.data);
      setClubs(clubsRes.data);
      setAssociations(assocRes.data);
      setJoinedClubs(joinedRes.data);
      setGuestUsers(guestRes.data);
      
      // Only show error if all requests failed
      if (usersRes.data.length === 0 && clubsRes.data.length === 0 && assocRes.data.length === 0) {
        setError('No data available. You may not have permission to view this information.');
      }
    } catch (err: any) {
      setError('Failed to load data. Please try again.');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.id) return;
    
    try {
      setDeleting(true);
      await api.delete(`/delete-user/${deleteDialog.id}`);
      setUsers(users.filter(u => u.id !== deleteDialog.id));
      setAssociations(associations.filter(a => a.user_id !== deleteDialog.id));
      setDeleteDialog({ open: false, id: null, name: null });
    } catch (err: any) {
      setError('Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleAssociate = async () => {
    if (!selectedUser || !selectedClub) return;

    try {
      setAssociating(true);
      await api.post('/associate-user-club', {
        user_id: selectedUser,
        club_id: selectedClub,
        promote_to_club_manager: promoteToClubManager,
      });

      // Refresh associations
      await fetchData();
      setAssociationDialog(false);
      setSelectedUser('');
      setSelectedClub('');
      setPromoteToClubManager(false);
    } catch (err: any) {
      setError('Failed to create association');
    } finally {
      setAssociating(false);
    }
  };

  const handleDissociate = async (userId: string, clubId: string) => {
    try {
      await api.post('/dissociate-user-club', {
        user_id: userId,
        club_id: clubId,
      });

      setAssociations(associations.filter(
        a => !(a.user_id === userId && a.club_id === clubId)
      ));
    } catch (err: any) {
      setError('Failed to remove association');
    }
  };

  const handleRoleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setRoleMenuAnchor(event.currentTarget);
    setRoleMenuUser(user);
  };

  const handleRoleMenuClose = () => {
    setRoleMenuAnchor(null);
    setRoleMenuUser(null);
  };

  const handleUpdateRole = async (updates: { user_type?: string; is_pilot?: boolean; is_platform_admin?: boolean }) => {
    if (!roleMenuUser) return;

    try {
      setUpdatingRole(true);
      await api.patch(`/users/${roleMenuUser.id}/role`, {
        user_id: roleMenuUser.id,
        ...updates,
      });
      setSuccess(t('users.roleUpdated'));
      await fetchData();
      handleRoleMenuClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('users.roleUpdateFailed'));
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleResendVerification = async (userEmail: string) => {
    try {
      // Pass current UI language, but backend will use user's preferred language if set
      const language = i18n.language === 'it' ? 'it' : 'en';
      await api.post('/resend-verification-email-by-email', { email: userEmail, language });
      setError(null);
      alert(t('users.verificationEmailSent'));
    } catch (err: any) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Failed to send verification email');
      }
    }
  };

  const filteredUsers = users.filter(user =>
    (user.name || '').toLowerCase().includes(searchUser.toLowerCase()) ||
    (user.username || '').toLowerCase().includes(searchUser.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(searchUser.toLowerCase()) ||
    (user.nickname || '').toLowerCase().includes(searchUser.toLowerCase())
  );

  // Categorize users (handle legacy users without user_type)
  const adminUsers = filteredUsers.filter(user => user.is_platform_admin);
  const clubManagers = filteredUsers.filter(user => 
    !user.is_platform_admin && 
    (user.user_type === 'club_manager' || (!user.user_type && !user.is_pilot)) && 
    !user.is_pilot
  );
  const pilots = filteredUsers.filter(user => 
    !user.is_platform_admin && 
    (user.user_type === 'pilot' || (user.is_pilot && user.user_type !== 'club_manager'))
  );
  const dualRoleUsers = filteredUsers.filter(user => 
    !user.is_platform_admin && 
    ((user.user_type === 'club_manager' && user.is_pilot) || (!user.user_type && user.is_pilot))
  );
  const allUsers = filteredUsers;
  
  // Filter guest users based on search
  const filteredGuestUsers = guestUsers.filter(user =>
    (user.name || '').toLowerCase().includes(searchUser.toLowerCase()) ||
    (user.nickname || '').toLowerCase().includes(searchUser.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(searchUser.toLowerCase()) ||
    (user.phone || '').toLowerCase().includes(searchUser.toLowerCase())
  );

  const getUsersByTab = () => {
    switch (tabValue) {
      case 0: return allUsers;
      case 1: return adminUsers;
      case 2: return clubManagers;
      case 3: return pilots;
      case 4: return dualRoleUsers;
      case 5: return [];
      default: return allUsers;
    }
  };
  
  const isGuestUsersTab = tabValue === 5;

  const currentUsers = getUsersByTab();
  const paginatedUsers = currentUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const paginatedGuestUsers = filteredGuestUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getUserAssociations = (userId: string) => {
    return associations.filter(a => a.user_id === userId);
  };

  const getUserJoinedClubs = (userId: string) => {
    return joinedClubs.filter(j => j.user_id === userId);
  };

  // Check if we're on the admins tab (tab 1) where we show associated clubs for management
  const isAdminsTab = tabValue === 1;

  const getUserTypeChip = (user: any) => {
    if (user.is_platform_admin) {
      return <Chip label={t('users.roles.platformAdmin')} color="secondary" size="small" icon={<AdminPanelSettings />} />;
    }
    // Handle dual role
    if ((user.user_type === 'club_manager' || !user.user_type) && user.is_pilot) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Chip label={t('users.roles.clubManager')} color="primary" size="small" icon={<Business />} />
          <Chip label={t('users.roles.pilot')} color="info" size="small" icon={<FlightTakeoff />} />
        </Box>
      );
    }
    // Handle club manager (including legacy users without user_type)
    if (user.user_type === 'club_manager' || (!user.user_type && !user.is_pilot)) {
      return <Chip label={t('users.roles.clubManager')} color="primary" size="small" icon={<Business />} />;
    }
    // Handle pilot
    if (user.user_type === 'pilot' || user.is_pilot) {
      return <Chip label={t('users.roles.pilot')} color="info" size="small" icon={<FlightTakeoff />} />;
    }
    return <Chip label={t('users.roles.unknown')} color="default" size="small" />;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const generateRandomPin = () => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  };

  const getGuestComplianceStatus = (user: GuestServiceUser) => {
    const fullyCompliant = user.terms_accepted && user.privacy_policy_accepted && user.shuttle_terms_accepted;
    const partiallyCompliant = user.terms_accepted || user.privacy_policy_accepted || user.shuttle_terms_accepted;

    if (fullyCompliant) {
      return <Chip label={t('guestUsers.compliance.full')} color="success" size="small" icon={<CheckCircle />} />;
    } else if (partiallyCompliant) {
      return <Chip label={t('guestUsers.compliance.partial')} color="warning" size="small" icon={<Warning />} />;
    } else {
      return <Chip label={t('guestUsers.compliance.none')} color="error" size="small" icon={<Cancel />} />;
    }
  };

  const handleResendPin = async () => {
    if (!pinDialog.userId || !pinDialog.newPin) return;
    
    try {
      setSendingPin(true);
      const response = await api.post(`/shuttle-pilots/${pinDialog.userId}/resend-pin`, {
        new_pin_code: pinDialog.newPin
      });
      
      setPinDialog({ open: false, userId: null, nickname: null, newPin: '' });
      setError(null);
      setSuccess(t('guestUsers.pinSentSuccess', { user: response.data.pilot_nickname }));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('guestUsers.errors.resendFailed'));
    } finally {
      setSendingPin(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <RequireEmailVerification>
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2
        }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 600 }}>
            {t('users.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={!isMobile && <LinkIcon fontSize="small" />}
              onClick={() => setAssociationDialog(true)}
              sx={{
                textTransform: 'none',
                px: { xs: 1.5, sm: 2 },
                py: 0.75,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {isMobile ? <LinkIcon fontSize="small" /> : 'Associate'}
            </Button>
            {currentUser?.is_platform_admin && (
              <Tooltip title="Force all users to re-accept agreements">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={!isMobile && <Gavel fontSize="small" />}
                  onClick={() => setForceAllReacceptOpen(true)}
                  sx={{
                    textTransform: 'none',
                    px: { xs: 1.5, sm: 2 },
                    py: 0.75,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    borderColor: 'error.main',
                    color: 'error.main',
                    '&:hover': {
                      borderColor: 'error.dark',
                      backgroundColor: 'rgba(211, 47, 47, 0.04)',
                    },
                  }}
                >
                  {isMobile ? <Gavel fontSize="small" /> : 'Force Re-accept'}
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData} size="small">
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            size="small"
            placeholder={t('users.search')}
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
            fullWidth={isMobile}
            sx={{ width: isMobile ? '100%' : 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Tabs for different user types */}
        <Paper elevation={3} sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => {
              setTabValue(newValue);
              setPage(0);
            }}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minWidth: isMobile ? 'auto' : 120,
                px: { xs: 1, sm: 2 },
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!isMobile && <Group fontSize="small" />}
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('users.tabs.allUsers')}</Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>All</Box>
                  <Chip label={allUsers.length} size="small" color="primary" sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }} />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!isMobile && <AdminPanelSettings fontSize="small" />}
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('users.tabs.admins')}</Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Admin</Box>
                  <Chip label={adminUsers.length} size="small" color="secondary" sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }} />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!isMobile && <Business fontSize="small" />}
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('users.tabs.clubManagers')}</Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Mgr</Box>
                  <Chip label={clubManagers.length} size="small" color="primary" sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }} />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!isMobile && <FlightTakeoff fontSize="small" />}
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('users.tabs.pilots')}</Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Pilot</Box>
                  <Chip label={pilots.length} size="small" color="info" sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }} />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!isMobile && <Group fontSize="small" />}
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('users.tabs.dualRole')}</Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Dual</Box>
                  <Chip label={dualRoleUsers.length} size="small" color="success" sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }} />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!isMobile && <Group fontSize="small" />}
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('users.tabs.guestUsers')}</Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Guest</Box>
                  <Chip label={filteredGuestUsers.length} size="small" color="info" sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }} />
                </Box>
              }
            />
          </Tabs>

          {/* Mobile Card View */}
          {isMobile ? (
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                {isGuestUsersTab ? (
                  paginatedGuestUsers.map((user) => (
                    <Card key={user.id} variant="outlined">
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2196f3' }}>
                              {user.nickname}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{user.name}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Chip label={user.service_tier} size="small" color={user.service_tier === 'premium' ? 'secondary' : 'default'} />
                            <IconButton size="small" color="primary" onClick={() => setPinDialog({ open: true, userId: user.id, nickname: user.nickname, newPin: generateRandomPin() })}>
                              <VpnKey fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {user.email_verified && <CheckCircle fontSize="small" color="success" sx={{ verticalAlign: 'middle', mr: 0.5 }} />}
                          {user.email}
                        </Typography>
                        {user.phone && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{user.phone}</Typography>}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                          {user.shuttle_access && <Chip label="Shuttle" size="small" color="primary" variant="outlined" />}
                          {user.reminder_access && <Chip label="Reminders" size="small" color="secondary" variant="outlined" />}
                          {getGuestComplianceStatus(user)}
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  paginatedUsers.map((user) => {
                    const userAssociations = getUserAssociations(user.id);
                    const userJoined = getUserJoinedClubs(user.id);
                    return (
                      <Card key={user.id} variant="outlined">
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: user.is_platform_admin ? 600 : 500 }}>
                                {user.name || user.username || 'N/A'}
                              </Typography>
                              {user.nickname && <Chip label={user.nickname} size="small" variant="outlined" sx={{ mt: 0.5 }} />}
                            </Box>
                            <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, id: user.id, name: user.name || user.username || 'N/A' })}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {user.email}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            {getUserTypeChip(user)}
                            {user.email_verified ? (
                              <Chip label="Verified" color="success" size="small" />
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Chip label="Unverified" color="warning" size="small" />
                                <IconButton size="small" onClick={() => handleResendVerification(user.email)} sx={{ p: 0.5, ml: 0.5 }}>
                                  <Email fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                          {userJoined.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">{t('users.table.joinedClubs')}:</Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {userJoined.map((joined) => (
                                  <Chip key={joined.club_id} label={joined.club_name} size="small" color="success" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Stack>
            </Box>
          ) : (
            /* Desktop Table View */
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {isGuestUsersTab ? (
                      <>
                        <TableCell>{t('guestUsers.table.nickname')}</TableCell>
                        <TableCell>{t('guestUsers.table.name')}</TableCell>
                        <TableCell>{t('guestUsers.table.email')}</TableCell>
                        <TableCell>{t('guestUsers.table.phone')}</TableCell>
                        <TableCell>{t('guestUsers.table.tier')}</TableCell>
                        <TableCell>{t('guestUsers.table.services')}</TableCell>
                        <TableCell>{t('guestUsers.table.usage')}</TableCell>
                        <TableCell>{t('guestUsers.table.compliance')}</TableCell>
                        <TableCell>{t('guestUsers.table.created')}</TableCell>
                        <TableCell align="center">{t('guestUsers.table.actions')}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{t('users.table.name')}</TableCell>
                        <TableCell>{t('users.table.nickname')}</TableCell>
                        <TableCell>{t('users.table.email')}</TableCell>
                        <TableCell>{t('users.table.userType')}</TableCell>
                        <TableCell>{t('users.table.emailVerified')}</TableCell>
                        {isAdminsTab && (
                          <TableCell>{t('users.table.associatedClubs')}</TableCell>
                        )}
                        <TableCell>{t('users.table.joinedClubs')}</TableCell>
                        <TableCell align="center">{t('users.table.actions')}</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isGuestUsersTab ? (
                    paginatedGuestUsers.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2196f3' }}>
                            {user.nickname}
                          </Typography>
                        </TableCell>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {user.email_verified && <CheckCircle fontSize="small" color="success" />}
                            {user.email}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {user.phone ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Phone fontSize="small" color="action" />
                              {user.phone}
                            </Box>
                          ) : (
                            <Typography color="text.secondary" variant="body2">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={user.service_tier} color={user.service_tier === 'premium' ? 'secondary' : 'default'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {user.shuttle_access && <Chip label={t('guestUsers.services.shuttle')} size="small" color="primary" icon={<LocalShipping />} variant="outlined" />}
                            {user.reminder_access && <Chip label={t('guestUsers.services.reminders')} size="small" color="secondary" icon={<Build />} variant="outlined" />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption">{t('guestUsers.usage.equipment')}: {user.equipment_count}{user.equipment_limit && ` / ${user.equipment_limit}`}</Typography>
                            <Typography variant="caption">{t('guestUsers.usage.bookings')}: {user.bookings_count}{user.shuttle_bookings_limit && ` / ${user.shuttle_bookings_limit}`}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{getGuestComplianceStatus(user)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{format(new Date(user.created_at), 'MMM d, yyyy')}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={t('guestUsers.resendPin')}>
                            <IconButton size="small" color="primary" onClick={() => setPinDialog({ open: true, userId: user.id, nickname: user.nickname, newPin: generateRandomPin() })}>
                              <VpnKey fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    paginatedUsers.map((user) => {
                      const userAssociations = getUserAssociations(user.id);
                      const userJoined = getUserJoinedClubs(user.id);
                      return (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: user.is_platform_admin ? 600 : 400 }}>
                              {user.name || user.username || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {user.nickname ? <Chip label={user.nickname} size="small" variant="outlined" /> : <Typography variant="caption" color="text.secondary">-</Typography>}
                          </TableCell>
                          <TableCell>{user.email || 'N/A'}</TableCell>
                          <TableCell>{getUserTypeChip(user)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {user.email_verified ? (
                                <Chip label="Verified" color="success" size="small" />
                              ) : (
                                <>
                                  <Chip label="Unverified" color="warning" size="small" />
                                  <Tooltip title="Resend verification email">
                                    <IconButton size="small" onClick={() => handleResendVerification(user.email)} sx={{ p: 0.5 }}>
                                      <Email fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                          {isAdminsTab && (
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {userAssociations.length > 0 ? (
                                  userAssociations.map((assoc) => (
                                    <Chip key={assoc.club_id} label={assoc.club_name} size="small" onDelete={() => handleDissociate(user.id, assoc.club_id)} deleteIcon={<LinkOff />} color="primary" variant="outlined" />
                                  ))
                                ) : (
                                  <Typography variant="caption" color="text.secondary">{t('users.noClubs')}</Typography>
                                )}
                              </Box>
                            </TableCell>
                          )}
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {userJoined.length > 0 ? (
                                userJoined.map((joined) => <Chip key={joined.club_id} label={joined.club_name} size="small" color="success" variant="outlined" />)
                              ) : (
                                <Typography variant="caption" color="text.secondary">{t('users.noMemberships')}</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title={t('users.manageRoles')}>
                                <IconButton size="small" onClick={(e) => handleRoleMenuOpen(e, user)} disabled={updatingRole}>
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('users.deleteUser')}>
                                <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, id: user.id, name: user.name || user.username || 'N/A' })}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={isGuestUsersTab ? filteredGuestUsers.length : currentUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, id: null, name: null })}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { mx: { xs: 1, sm: 2 } }
          }}
        >
          <DialogTitle>{t('users.deleteConfirmTitle')}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              {t('users.deleteConfirmMessage')} "<strong>{deleteDialog.name}</strong>"?
            </Typography>
            <Typography variant="body2" color="warning.main">
              {t('users.deleteWarning')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
            <Button
              onClick={() => setDeleteDialog({ open: false, id: null, name: null })}
              disabled={deleting}
            >
              {t('users.cancel')}
            </Button>
            <Button
              onClick={handleDeleteUser}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} /> : null}
            >
              {deleting ? t('common.deleting') : t('users.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Association Dialog */}
        <Dialog
          open={associationDialog}
          onClose={() => setAssociationDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { mx: { xs: 1, sm: 2 } }
          }}
        >
          <DialogTitle>{t('users.associateWithClub')}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('users.selectUser')}</InputLabel>
                <Select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  label={t('users.selectUser')}
                >
                  {users
                    .filter((user) => !user.is_platform_admin) // Exclude platform admins, allow all other users including pilots
                    .map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name || user.username} ({user.email})
                        {user.is_pilot && ' 🛩️'}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('users.selectClub')}</InputLabel>
                <Select
                  value={selectedClub}
                  onChange={(e) => setSelectedClub(e.target.value)}
                  label={t('users.selectClub')}
                >
                  {clubs.map((club) => (
                    <MenuItem key={club.record_id} value={club.record_id}>
                      {club.club_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={promoteToClubManager}
                    onChange={(e) => setPromoteToClubManager(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('users.promoteToClubManager')}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
            <Button onClick={() => setAssociationDialog(false)}>{t('users.cancel')}</Button>
            <Button
              onClick={handleAssociate}
              variant="contained"
              disabled={!selectedUser || !selectedClub || associating}
            >
              {associating ? <CircularProgress size={24} /> : t('users.associate')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Force All Re-accept Dialog */}
        <ForceAllReacceptDialog
          open={forceAllReacceptOpen}
          onClose={() => setForceAllReacceptOpen(false)}
          onSuccess={() => {
            fetchData();
            setError(null);
          }}
        />

        {/* Resend PIN Dialog */}
        <Dialog
          open={pinDialog.open}
          onClose={() => setPinDialog({ open: false, userId: null, nickname: null, newPin: '' })}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { mx: { xs: 1, sm: 2 } }
          }}
        >
          <DialogTitle>{t('guestUsers.resendPinTitle')}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              {t('guestUsers.resendPinMessage', { nickname: pinDialog.nickname })}
            </Typography>
            <TextField
              fullWidth
              label={t('guestUsers.newPin')}
              value={pinDialog.newPin}
              onChange={(e) => setPinDialog({ ...pinDialog, newPin: e.target.value })}
              inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
              helperText={t('guestUsers.pinHelperText')}
            />
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
            <Button
              onClick={() => setPinDialog({ open: false, userId: null, nickname: null, newPin: '' })}
              disabled={sendingPin}
            >
              {t('users.cancel')}
            </Button>
            <Button
              onClick={handleResendPin}
              variant="contained"
              color="primary"
              disabled={!pinDialog.newPin || pinDialog.newPin.length !== 4 || sendingPin}
              startIcon={sendingPin ? <CircularProgress size={20} /> : <VpnKey />}
            >
              {sendingPin ? t('common.sending') : t('guestUsers.sendPin')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Role Management Menu */}
        <Menu
          anchorEl={roleMenuAnchor}
          open={Boolean(roleMenuAnchor)}
          onClose={handleRoleMenuClose}
        >
          {roleMenuUser && !roleMenuUser.is_platform_admin && (
            <MenuItem onClick={() => handleUpdateRole({ is_platform_admin: true })}>
              <ListItemIcon><AdminPanelSettings fontSize="small" color="secondary" /></ListItemIcon>
              <ListItemText>{t('users.promoteToAdmin')}</ListItemText>
            </MenuItem>
          )}
          {roleMenuUser && roleMenuUser.is_platform_admin && roleMenuUser.id !== currentUser?.id && (
            <MenuItem onClick={() => handleUpdateRole({ is_platform_admin: false })}>
              <ListItemIcon><PersonRemove fontSize="small" color="warning" /></ListItemIcon>
              <ListItemText>{t('users.demoteFromAdmin')}</ListItemText>
            </MenuItem>
          )}
          {roleMenuUser && roleMenuUser.user_type !== 'club_manager' && (
            <MenuItem onClick={() => handleUpdateRole({ user_type: 'club_manager' })}>
              <ListItemIcon><Business fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText>{t('users.promoteToClubManager')}</ListItemText>
            </MenuItem>
          )}
          {roleMenuUser && roleMenuUser.user_type === 'club_manager' && (
            <MenuItem onClick={() => handleUpdateRole({ user_type: 'pilot' })}>
              <ListItemIcon><PersonRemove fontSize="small" color="warning" /></ListItemIcon>
              <ListItemText>{t('users.demoteFromClubManager')}</ListItemText>
            </MenuItem>
          )}
          {roleMenuUser && !roleMenuUser.is_pilot && (
            <MenuItem onClick={() => handleUpdateRole({ is_pilot: true })}>
              <ListItemIcon><FlightTakeoff fontSize="small" color="info" /></ListItemIcon>
              <ListItemText>{t('users.makePilot')}</ListItemText>
            </MenuItem>
          )}
          {roleMenuUser && roleMenuUser.is_pilot && (
            <MenuItem onClick={() => handleUpdateRole({ is_pilot: false })}>
              <ListItemIcon><PersonRemove fontSize="small" color="warning" /></ListItemIcon>
              <ListItemText>{t('users.removePilot')}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Container>
    </RequireEmailVerification>
  );
};

export default UsersPage;